import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LaptopChromebookIcon from "@mui/icons-material/LaptopChromebook";
import { fetchItCategory, deleteItCategory, fetchItType } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ItCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [itTypeName, setItTypeName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/it-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    try {
      const d = await fetchItCategory(categoryId);
      setData(d);
      if (d.itTypeId) {
        const it = await fetchItType(d.itTypeId);
        setItTypeName(it.itTypeName || "");
      }
    }
    catch (e) { notify.apiError(e); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteItCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/it-categories`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("itCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("itCategory.itType")} value={itTypeName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("itCategory.name")} value={data.itCategoryName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.url")} value={data.url} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.description")} value={data.itCategoryDescription} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<AssessmentIcon />}
          onClick={() => navigate(`${basePath}/classification-criteria`)}>
          {t("itCategory.classificationCriteriaValues")}
        </Button>
        <Button variant="outlined" startIcon={<LaptopChromebookIcon />}
          onClick={() => navigate(`${basePath}/application-categories`)}>
          {t("itCategory.applicationCategories")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/it-categories`)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
